import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  Award,
  Presentation,
  Megaphone,
  CheckCircle2,
  MapPin,
  Plane,
} from "lucide-react";
import Seo from "@/components/Seo";
import { MICE_IMG, SERVICE_IMAGES } from "@/lib/media";

const BREADCRUMB = [
  { name: "Home", path: "/" },
  { name: "MICE Services", path: "/mice-services" },
];

const PILLARS = [
  {
    icon: Building2,
    title: "Venue Sourcing & Contracting",
    copy:
      "We map your brief to the right venue across 7 destinations — convention halls, integrated resorts, ballrooms, harbour properties or full hotel buyouts — and own every contract negotiation.",
  },
  {
    icon: Award,
    title: "Accommodation & Hotel Blocks",
    copy:
      "From 50-room mid-market blocks to 2,500-room multi-property buyouts, we contract and manage every rooming list, upgrade, comp and amenity.",
  },
  {
    icon: Presentation,
    title: "Event Production & AV",
    copy:
      "Stage design, LED walls, lighting design, simultaneous interpretation booths, conference-grade audio, video crews, content production and mobile-app delegate management.",
  },
  {
    icon: Megaphone,
    title: "F&B, Gala Dinners & Team-Building",
    copy:
      "Themed gala dinners with custom set design, halal/kosher/dietary compliance, branded F&B activations, and team-building from amazing-race city challenges to private-island survival programmes.",
  },
];

const DESTINATIONS = [
  "Dubai",
  "Singapore",
  "Malaysia",
  "Thailand",
  "Hong Kong",
  "Vietnam",
  "Philippines",
];

const WHY = [
  "20+ years of operating experience across 7 destinations",
  "140,000+ travellers managed — Fortune 500 references on request",
  "48-hour proposal turnaround on most briefs",
  "Dedicated account manager + on-ground operations team",
  "Direct relationships with premium venues — better rates, better service",
  "End-to-end accountability — one contract, one P&L, one team",
];

const FAQS = [
  {
    q: "What does MICE stand for?",
    a: "MICE stands for Meetings, Incentives, Conferences, and Exhibitions. It refers to the segment of business travel and events where groups of corporate delegates travel together for a structured business purpose — board offsites and leadership workshops (Meetings), reward and motivation trips for top performers (Incentives), large-scale knowledge-sharing or industry events (Conferences), and trade-show or exhibition participation (Exhibitions).",
  },
  {
    q: "How do I choose a MICE company in Dubai?",
    a: "Look for four things. First, depth of local relationships — a MICE partner who has worked Dubai for at least 10 years will have negotiated rates with the venues you actually need. Second, end-to-end capability — venue, accommodation, AV, F&B, transport and creative production under one accountable contract beats coordinating five vendors. Third, references from comparable-scale events. Fourth, a named senior account manager who owns your programme from brief to reconciliation. Trawelldmc checks all four boxes and we're happy to share references on request.",
  },
  {
    q: "What is the typical lead time for a MICE event?",
    a: "For large MICE events of 1,000+ pax at top-tier venues like Marina Bay Sands, Dubai World Trade Centre or HKCEC, 9–12 months of lead time is recommended. For mid-sized corporate conferences of 300–800 pax, 4–6 months is comfortable. For smaller board offsites or incentive groups of 40–150 pax, we can often turn around proposals within 4–8 weeks. The most critical constraint is hotel inventory during peak dates (Q4 in Dubai, January in Singapore, late November in Bangkok) — earlier briefs always get better rates.",
  },
  {
    q: "Can Trawelldmc handle both travel logistics and event production?",
    a: "Yes — that's exactly what differentiates us from a pure travel agency or a pure event production house. We own the full stack. Travel logistics (flight blocks, visa coordination, airport transfers, hotel contracting, rooming lists) and event production (creative concept, stage design, AV, lighting, content, entertainment, simultaneous interpretation) sit under one programme manager and one P&L. That means no finger-pointing between vendors, no commercial gaps, and one team accountable end-to-end.",
  },
];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function MiceServices() {
  return (
    <div data-testid="mice-services-page">
      <Seo
        path="/mice-services"
        title="MICE Services in Dubai & Southeast Asia | Trawelldmc"
        description="End-to-end MICE management — Dubai, Singapore, Thailand & more. Meetings, incentives, conferences, exhibitions. 20+ years. 140,000+ travellers. RFP welcome."
        breadcrumb={BREADCRUMB}
        extraJsonLd={FAQ_JSONLD}
      />

      <section className="relative">
        <img
          src={SERVICE_IMAGES.miceServices}
          alt="Trawelldmc MICE event management — Dubai and Southeast Asia"
          width="1920"
          height="1080"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(11,25,44,0.92) 0%, rgba(11,25,44,0.55) 100%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-32 md:py-44 text-white">
          <div className="max-w-3xl">
            <div className="overline !text-[#FF5238] mb-5">MICE Specialists · 20 Years</div>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[1.04]">
              MICE Event Management <br />
              <span className="text-[#FF5238]">Dubai & Southeast Asia</span>
            </h1>
            <p className="text-white/80 mt-8 text-lg max-w-2xl leading-relaxed">
              Meetings. Incentives. Conferences. Exhibitions. Engineered
              end-to-end by the team that's served 140,000+ travellers across
              seven destinations.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/inquiry?type=MICE" className="btn-primary" data-testid="mice-svc-cta-rfp">
                Submit an RFP <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/quote" className="btn-secondary" data-testid="mice-svc-cta-ai-quote">
                Instant AI estimate
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline mb-4">What is MICE</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Meetings. Incentives. <br />Conferences. Exhibitions.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-slate-700 text-lg leading-relaxed">
            <p>
              MICE — short for Meetings, Incentives, Conferences and
              Exhibitions — is the segment of business travel where corporate
              groups travel together for a structured business outcome.
              Meetings cover board offsites and leadership workshops.
              Incentives reward and motivate top performers. Conferences
              convene hundreds or thousands of delegates for industry events.
              Exhibitions activate trade-show presence and product launches.
            </p>
            <p>
              Trawelldmc is a MICE-first DMC. 70% of our volume is MICE work.
              That focus shows up in how we operate — we have full-time
              creative production, AV, content, and ground-ops teams (not
              part-time partner agencies); we maintain rate agreements with
              every major MICE venue across our seven destinations; and our
              account managers are MICE specialists, not generalist travel
              agents. The result is faster proposals, tighter budgets, and
              event experiences that consistently exceed corporate buyers'
              expectations.
            </p>
            <p>
              We've delivered events ranging from 20-pax CEO retreats at
              Amanpulo to 2,500-pax dealer congresses at Marina Bay Sands.
              The same playbook, scaled appropriately. Brief, propose,
              produce, deliver — owned end-to-end by one accountable team.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-[#FDFCF8] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="overline mb-4">Full-stack offering</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              What we deliver on every MICE brief.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {PILLARS.map((p) => (
              <div key={p.title} className="card-flat p-8">
                <p.icon className="h-8 w-8 text-[#FF5238]" />
                <h3 className="font-heading text-xl font-medium tracking-tight text-[#0B192C] mt-5">{p.title}</h3>
                <p className="text-slate-600 mt-3 leading-relaxed">{p.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="mb-16 max-w-2xl">
          <div className="overline mb-4">Destinations covered</div>
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
            Seven destinations. <br />One accountable partner.
          </h2>
          <p className="text-slate-600 mt-6 leading-relaxed">
            We operate MICE programmes across Dubai and six Southeast Asian
            destinations. Click any destination below for a dedicated
            country-level page with venues, pricing context and case studies.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {DESTINATIONS.map((d) => {
            const slug = d.toLowerCase().replace(/ /g, "-");
            return (
              <Link
                key={d}
                to={`/${slug}-dmc`}
                className="card-flat p-5 text-center hover:border-[#FF5238] transition-colors"
                data-testid={`mice-dest-${slug}`}
              >
                <MapPin className="h-5 w-5 text-[#FF5238] mx-auto mb-2" />
                <div className="font-medium text-[#0B192C]">{d}</div>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="bg-[#0B192C] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline !text-[#FF5238] mb-4">Why Trawelldmc</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight">
              Why Trawelldmc <br /><span className="text-[#FF5238]">for MICE.</span>
            </h2>
          </div>
          <ul className="lg:col-span-7 space-y-5">
            {WHY.map((w, i) => (
              <li key={w} className="flex gap-4 items-start border-t border-white/15 pt-5" data-testid={`mice-why-${i}`}>
                <CheckCircle2 className="h-6 w-6 text-[#FF5238] flex-shrink-0 mt-0.5" />
                <span className="text-lg text-white/90 leading-snug">{w}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="mb-16 max-w-2xl">
          <div className="overline mb-4">FAQ</div>
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
            Frequently Asked Questions
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
          {FAQS.map((f, i) => (
            <div key={f.q} data-testid={`mice-svc-faq-${i}`}>
              <h3 className="font-heading text-xl font-medium text-[#0B192C] tracking-tight">{f.q}</h3>
              <p className="text-slate-600 mt-3 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#FDFCF8] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="overline mb-4">Submit your brief</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Ready to brief <br /><span className="text-[#FF5238]">your next MICE event?</span>
            </h2>
            <p className="text-slate-600 mt-6 max-w-xl text-lg leading-relaxed">
              Submit your RFP. We respond within 48 hours with a costed,
              themed proposal — venue, accommodation, production, F&B and
              transport, all under one accountable team.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-4">
            <Link to="/inquiry?type=MICE" className="btn-primary w-full justify-center" data-testid="mice-svc-bottom-rfp">
              Submit an RFP <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" className="btn-secondary w-full justify-center">Contact our team</Link>
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-16">
          <div className="overline mb-6">Explore related</div>
          <nav className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" aria-label="Related Trawelldmc pages">
            <Link to="/corporate-travel" className="group flex items-start gap-3">
              <Award className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">Corporate Travel</div>
                <div className="text-sm text-slate-500 mt-1">Group travel management</div>
              </div>
            </Link>
            <Link to="/incentive-travel" className="group flex items-start gap-3">
              <Award className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">Incentive Travel</div>
                <div className="text-sm text-slate-500 mt-1">Reward top performers</div>
              </div>
            </Link>
            <Link to="/dubai-dmc" className="group flex items-start gap-3">
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">DMC Dubai</div>
                <div className="text-sm text-slate-500 mt-1">GCC HQ · MICE & corporate</div>
              </div>
            </Link>
            <Link to="/" className="group flex items-start gap-3">
              <Plane className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">Trawelldmc Home</div>
                <div className="text-sm text-slate-500 mt-1">All destinations & services</div>
              </div>
            </Link>
          </nav>
        </div>
      </section>
    </div>
  );
}
